// Cloudflare Turnstile ("I'm human" check) for sign-up and the free starter
// avatar claim. The script loads only from challenges.cloudflare.com, and only
// when a site key is configured (VITE_TURNSTILE_SITE_KEY). Without one the
// widget renders nothing and logs a warning — the forms work as before (fail
// open) until she adds the keys.
import React, { useEffect, useRef } from 'react';

// The site key is public by design (it ships in every page); the secret lives in Cloudflare.
export const TURNSTILE_SITE_KEY: string = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined)
  || (import.meta.env.PROD ? '0x4AAAAAAFkGy-ZQIrgLJP3N' : '');
export const turnstileEnabled = !!TURNSTILE_SITE_KEY;

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
}
declare global { interface Window { turnstile?: TurnstileApi } }

let scriptPromise: Promise<TurnstileApi> | null = null;
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile failed to load')));
      s.onerror = () => { scriptPromise = null; reject(new Error('Turnstile failed to load')); };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

let warned = false;

export interface TurnstileWidgetProps {
  /** A fresh token, or null when it expired / errored / was reset. */
  onToken: (token: string | null) => void;
  /** Change this number to get a new token (tokens are single-use). */
  resetKey?: number;
  action?: string;
  className?: string;
}

const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({ onToken, resetKey = 0, action, className }) => {
  const box = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const cb = useRef(onToken);
  cb.current = onToken;

  useEffect(() => {
    if (!turnstileEnabled) {
      if (!warned) { warned = true; console.warn('[turnstile] VITE_TURNSTILE_SITE_KEY is not set; the human check is off'); }
      return;
    }
    let cancelled = false;
    loadTurnstile().then((ts) => {
      if (cancelled || !box.current) return;
      const theme = document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
      widgetId.current = ts.render(box.current, {
        sitekey: TURNSTILE_SITE_KEY,
        action,
        theme,
        callback: (t: string) => cb.current(t),
        'expired-callback': () => cb.current(null),
        'error-callback': () => cb.current(null),
      });
    }).catch((err) => console.warn('[turnstile]', err?.message || err));
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) { try { window.turnstile.remove(widgetId.current); } catch { /* gone */ } }
      widgetId.current = null;
    };
  }, [action]);

  useEffect(() => {
    if (!resetKey || !widgetId.current || !window.turnstile) return;
    cb.current(null);
    try { window.turnstile.reset(widgetId.current); } catch { /* gone */ }
  }, [resetKey]);

  if (!turnstileEnabled) return null;
  return <div ref={box} className={className} />;
};

export default TurnstileWidget;
